import { useSortable } from '@dnd-kit/react/sortable'
import { DotsSixVerticalIcon, DotsThreeIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { MouseEvent, PointerEvent, ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { useIsTruncated } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'

import * as S from './linked-column.styled'
import { getRowAttributes } from './linked-columns.attributes'

const ROW_OWN_CLICK_ATTRIBUTE = 'data-row-own-click'

interface LinkedRowProps {
  dndId: string
  index: number
  leading?: ReactNode
  menuItems?: DropdownMenuItem[]
  position?: number
  selected: boolean
  subtitle: string
  subtitleEnd?: ReactNode
  title: string
  onEdit?: VoidFunction
  sortable: boolean
  onSelect: VoidFunction
}

export const LinkedRow = ({
  dndId,
  index,
  leading,
  menuItems,
  position,
  selected,
  subtitle,
  subtitleEnd,
  sortable,
  title,
  onEdit,
  onSelect,
}: LinkedRowProps) => {
  const { t } = useTranslation('common')
  const { handleRef, ref } = useSortable({ disabled: !sortable, id: dndId, index })
  // Note: a reorder re-inserts the element, which would replay the entrance animation
  const [animate, setAnimate] = useState(true)
  const { check, isTruncated, ref: titleRef } = useIsTruncated<HTMLElement>()
  const fullTitle = position === undefined ? title : `${position}. ${title}`

  // Note: a press that starts on the drag handle or an action button is theirs, even when released
  // elsewhere in the row (a drag dropped off the handle fires its click on the row itself)
  const pressedOwnClickRef = useRef(false)

  const isOwnClickTarget = (target: EventTarget) =>
    !!(target as HTMLElement).closest(`[${ROW_OWN_CLICK_ATTRIBUTE}]`)

  const handleRowPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    pressedOwnClickRef.current = isOwnClickTarget(event.target)
  }

  // Note: the whole row selects, not just the text. A portaled menu's clicks bubble through React but
  // are not inside the row's DOM
  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    const pressedOwnClick = pressedOwnClickRef.current
    pressedOwnClickRef.current = false
    if (!event.currentTarget.contains(event.target as Node)) return
    if (pressedOwnClick || isOwnClickTarget(event.target)) return
    onSelect()
  }

  return (
    <S.ListItem
      ref={ref}
      animate={animate}
      index={index}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) setAnimate(false)
      }}
    >
      <S.Row
        $selected={selected}
        {...getRowAttributes(dndId, selected)}
        onClick={handleRowClick}
        onPointerDown={handleRowPointerDown}
      >
        {sortable && (
          <S.DragHandle
            ref={handleRef}
            {...{ [ROW_OWN_CLICK_ATTRIBUTE]: '' }}
            aria-label={t('linkedColumns.drag')}
          >
            <Icon icon={DotsSixVerticalIcon} size="1.6rem" />
          </S.DragHandle>
        )}

        {leading}

        <S.RowMain aria-current={selected ? 'true' : undefined} type="button">
          <S.RowTitleTooltip
            fullWidth
            disabled={!isTruncated}
            trigger={
              <Typography
                ref={titleRef}
                breakLines={1}
                color="black800"
                size="text-medium"
                weight="regular"
                onMouseEnter={check}
              >
                {fullTitle}
              </Typography>
            }
          >
            {fullTitle}
          </S.RowTitleTooltip>
          <S.Subtitle>
            <Typography as="span" color="black500" size="text-small">
              {subtitle}
            </Typography>
            {subtitleEnd && (
              <>
                <Typography as="span" color="black500" size="text-small">
                  ·
                </Typography>
                {subtitleEnd}
              </>
            )}
          </S.Subtitle>
        </S.RowMain>

        {(onEdit || menuItems) && (
          <S.RowActions {...{ [ROW_OWN_CLICK_ATTRIBUTE]: '' }}>
            {onEdit && (
              <Button
                onlyBox
                aria-label={t('actions.edit')}
                size="small"
                use="primary-light"
                onClick={onEdit}
              >
                <Icon icon={PencilSimpleIcon} />
              </Button>
            )}
            {menuItems && (
              <DropdownMenu
                items={menuItems}
                trigger={() => (
                  <Button
                    onlyBox
                    aria-label={t('linkedColumns.menu')}
                    size="small"
                    use="primary-light"
                  >
                    <Icon icon={DotsThreeIcon} weight="bold" />
                  </Button>
                )}
              />
            )}
          </S.RowActions>
        )}
      </S.Row>
    </S.ListItem>
  )
}
